// src/components/LidarViewer.jsx
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom"; // IMPORT REQUIRED FOR PORTAL

// --- API Configuration ---
const RAW_API_BASE = process.env.REACT_APP_API_BASE || "/api";
const API_BASE = String(RAW_API_BASE).replace(/\/+$/, "");

// --- Styles ---
const styles = `
  /* 1. THE BACKDROP: High Z-Index to cover Sidebar */
  .lidar-backdrop {
    position: fixed;
    top: 0; left: 0; width: 100vw; height: 100vh;
    z-index: 9999; /* Higher than sidebar (usually 1000-1100) */
    
    /* FIX: Semi-transparent black instead of solid #000 */
    background-color: rgba(0, 0, 0, 0.85); 
    
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(2px); /* Optional: adds a nice blur to background */
  }

  /* 2. THE WINDOW */
  .lidar-modal-window {
    width: 70%;
    height: 90%;
    display: flex;
    flex-direction: column;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 0 25px rgba(0, 0, 0, 0.7);
    position: relative;
    border: 1px solid #333;
  }

  .lidar-header {
    height: 60px; background: #000;
    display: flex; align-items: center;
    justify-content: space-between;
    padding: 0 20px; color: white;
    border-bottom: 1px solid #333;
    z-index: 10;
  }
  .lidar-content {
    flex: 1; position: relative;
    width: 100%; height: 100%;
    background: #000; 
    overflow: hidden;
  }
  
  /* --- POTREE STRUCTURE --- */
  .potree_container {
    position: absolute; width: 100%; height: 100%; left: 0px; top: 0px;
    overflow: hidden; 
  }
  
  .potree_container img {
    max-width: none !important;
    height: auto !important;
    box-sizing: content-box !important;
  }

  #potree_render_area {
    position: absolute; width: 100%; height: 100%; top: 0; left: 0; background: #000;
  }
  
  #potree_sidebar_container {
    position: absolute; width: 300px; height: 100%; right: 0px; top: 0px;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 1000;
    overflow-y: auto;
    border-left: 1px solid #444;
  }

  #potree_menu_toggle {
    display: none !important;
  }

  #potree_sidebar_container {
    left: 0;
    right: auto;
    border-left: none;
    border-right: 1px solid #444;
  }
`;

const LidarViewer = ({ buildingId, organization, onClose }) => {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [error, setError] = useState(null);

  const renderAreaRef = useRef(null);
  const sidebarRef = useRef(null);
  const viewerRef = useRef(null);

  // --- Fetch User ---
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoadingUser(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setUser(await res.json());
        }
      } catch (e) {
        console.error("Failed to fetch user", e);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
  }, []);

  // --- Init Potree ---
  useEffect(() => {
    const Potree = window.Potree;

    if (!Potree) {
      setError("Potree library not loaded");
      return;
    }

    if (!buildingId || !renderAreaRef.current) return;

    let isMounted = true; 

    const load = async () => {
      const token = localStorage.getItem("token");

      try {
        const res = await fetch(
          `${API_BASE}/location/${buildingId}/lidar`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!res.ok) throw new Error("No LiDAR data found");

        const { exists, metadataUrl } = await res.json();
        if (!exists || !metadataUrl) throw new Error("LiDAR metadata missing");

        const isProduction = process.env.NODE_ENV === 'production';
        const urlToLoad = isProduction 
          ? metadataUrl.replace(/^https?:\/\/[^/]+/, "") 
          : metadataUrl; 

        if (!isMounted) return;

        if (viewerRef.current) viewerRef.current = null;
        if (renderAreaRef.current) renderAreaRef.current.innerHTML = "";
        if (sidebarRef.current) sidebarRef.current.innerHTML = "";

        const viewer = new Potree.Viewer(renderAreaRef.current);
        viewerRef.current = viewer;

        viewer.setEDLEnabled(true);
        viewer.setFOV(60);
        viewer.setPointBudget(3_000_000);
        viewer.setBackground("black");

        const loadCloud = () => {
            Potree.loadPointCloud(urlToLoad, "LiDAR", (e) => {
                if(!isMounted || !viewerRef.current) return;
                const pc = e.pointcloud;
                viewer.scene.addPointCloud(pc);
                pc.material.size = 1;
                pc.material.pointSizeType = Potree.PointSizeType.ADAPTIVE;
                pc.material.activeAttributeName = "rgba";
                viewer.fitToScreen();
            });
        };

        if (document.getElementById("potree_sidebar_container")) {
            viewer.loadGUI(() => {
                if(!isMounted) return;
                viewer.setLanguage('en');
                viewer.setBackground("black");
                
                const $ = window.jQuery;
                if ($) {
                    $("#potree_sidebar_container").show();
                    $("#menu_appearance").next().slideDown();
                    $("#menu_tools").next().slideDown();
                    $("#menu_scene").next().slideDown();
                }
                loadCloud();
            });
        } else {
            loadCloud();
        }

      } catch (err) {
        console.error("Failed to load LiDAR", err);
        if(isMounted) setError(err.message || "Failed to load LiDAR viewer");
      }
    };

    load();

    return () => {
      isMounted = false;
      viewerRef.current = null;
    };
  }, [buildingId]);

  const displayOrg = organization || (user ? user.organization : "Unknown Org");
  const displayEmail = user ? user.email : "Unknown User";

  // --- RENDER CONTENT ---
  const content = (
    <div className="lidar-backdrop">
      <style>{styles}</style>
      <div className="lidar-modal-window">
          {/* Header */}
          <div className="lidar-header">
              <div className="d-flex align-items-center gap-3">
                  <i className="bi bi-box-seam text-primary fs-4"></i>
                  <div>
                  <span className="fw-bold d-block">LiDAR Viewer</span>
                  <span className="text-muted small">ID: {buildingId}</span>
                  </div>

                  <div className="d-none d-md-block ms-4 ps-4 border-start border-secondary text-white-50 small">
                  <div>
                      <i className="bi bi-building me-1"></i>
                      Org: <span className="text-white">{displayOrg}</span>
                  </div>
                  <div>
                      <i className="bi bi-person me-1"></i>
                      User:{" "}
                      <span className="text-white">
                      {loadingUser ? "..." : displayEmail}
                      </span>
                  </div>
                  </div>
              </div>

              <button
                  className="btn btn-sm btn-outline-light border-0"
                  onClick={onClose}
                  style={{ fontSize: "1.5rem" }}
              >
                  <i className="bi bi-x"></i>
              </button>
          </div>

          {/* Viewer Content */}
          <div className="lidar-content">
          {error ? (
              <div className="text-white text-center mt-5" style={{zIndex: 2000, position:'relative'}}>
              <i className="bi bi-exclamation-triangle fs-1"></i>
              <p className="mt-3">{error}</p>
              </div>
          ) : (
              <div className="potree_container">
              <div id="potree_render_area" ref={renderAreaRef}></div>
              <div id="potree_sidebar_container" ref={sidebarRef}></div>
              </div>
          )}
          </div>
      </div>
    </div>
  );

  // 🔥 CRITICAL FIX: Use Portal to attach to body
  return ReactDOM.createPortal(content, document.body);
};

export default LidarViewer;